/* A single, short pause at the DESIGN chapter. Native navigation and reduced
   motion always take priority; the unenhanced section remains fully readable. */
(() => {
  'use strict';

  const root = document.documentElement;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const interactive = 'a, button, input, select, textarea, summary, [role="button"], [contenteditable]';
  const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
  const lockClass = 'design-intro-scroll-locked';
  const headingDelay = 750;

  function mount(scene) {
    if (scene.dataset.designIntroMounted) return;
    scene.dataset.designIntroMounted = 'true';

    const media = scene.querySelector('.design-intro__media');
    const image = scene.querySelector('.design-intro__image');
    const copy = scene.querySelector('.design-intro__copy');
    const cue = scene.querySelector('[data-design-intro-next]');
    const header = document.querySelector('[data-header]');
    const purchaseBar = document.querySelector('[data-purchase-bar]');
    const entranceEvents = new AbortController();
    const lifetimeEvents = new AbortController();
    const animations = [];
    const timers = new Set();
    let state = 'waiting';
    let lastY = window.scrollY;
    let finger = null;
    let lock = null;
    let lockObserver = null;
    let decodedSource = null;

    function preparePhotograph() {
      if (!image?.complete || image.naturalWidth <= 0) return;
      const source = image.currentSrc || image.src;
      if (!image.decode) { decodedSource = source; return; }
      // Resolve the selected <picture> source before acquiring a lock. Decoding
      // is never awaited while readers are held at the section.
      image.decode().then(() => {
        if ((image.currentSrc || image.src) === source) decodedSource = source;
      }).catch(() => {});
    }

    function bounds() {
      const height = window.visualViewport?.height || innerHeight;
      const headerRect = header?.getBoundingClientRect();
      const headerVisible = headerRect && headerRect.height > 0 &&
        getComputedStyle(header).visibility !== 'hidden';
      const top = headerVisible ? Math.max(0, Math.min(height, headerRect.bottom)) : 0;
      const barRect = purchaseBar?.getBoundingClientRect();
      const barVisible = barRect && barRect.height > 0 && barRect.top > top &&
        barRect.top < height && getComputedStyle(purchaseBar).visibility !== 'hidden';
      return { top, bottom: barVisible ? barRect.top : height };
    }

    function sizeSection() {
      const viewport = bounds();
      scene.style.setProperty('--design-intro-view-height', `${Math.max(0, viewport.bottom - viewport.top)}px`);
      return viewport;
    }

    // Only the properties owned by this controller are restored. A cart or
    // dialog that has since changed one keeps its new value and priority.
    function ownStyle(element, property, value) {
      const previous = element.style.getPropertyValue(property);
      const priority = element.style.getPropertyPriority(property);
      element.style.setProperty(property, value, 'important');
      const applied = element.style.getPropertyValue(property);
      return () => {
        if (element.style.getPropertyValue(property) !== applied ||
            element.style.getPropertyPriority(property) !== 'important') return;
        if (previous) element.style.setProperty(property, previous, priority);
        else element.style.removeProperty(property);
      };
    }

    function scrollExactly(left, top) {
      const restore = ownStyle(root, 'scroll-behavior', 'auto');
      try { window.scrollTo({ left, top, behavior: 'auto' }); }
      finally { restore(); }
    }

    function release() {
      if (!lock) return;
      const saved = lock;
      lock = null;
      lockObserver?.disconnect();
      lockObserver = null;
      saved.restore.reverse().forEach(restore => restore());
      root.classList.remove(lockClass);
      scrollExactly(saved.x, saved.y);
      lastY = window.scrollY;
    }

    function finish() {
      if (state === 'complete') return;
      state = 'complete';
      timers.forEach(clearTimeout);
      timers.clear();
      entranceEvents.abort();
      scene.classList.remove('design-intro--armed', 'design-intro--running');
      scene.classList.add('design-intro--complete');
      animations.forEach(animation => animation.cancel());
      release();
    }

    function allowScroll() {
      if (state !== 'running') return;
      // The heading's first frame releases navigation. The photo and copy keep
      // animating, without the input guards pulling the reader back to the anchor.
      state = 'revealing';
      scene.classList.add('design-intro--unlocked');
      release();
    }

    function wait(callback, duration) {
      const timer = setTimeout(() => {
        timers.delete(timer);
        callback();
      }, duration);
      timers.add(timer);
    }

    function openingActive() {
      return ['hero-entrance-pending', 'hero-entrance-running', 'is-splashing']
        .some(name => root.classList.contains(name));
    }

    function modalVisible() {
      return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]')).some(element => {
        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        // The closed Shopify cart remains mounted outside the content viewport.
        // Only a dialog actually on screen should take precedence over the intro.
        const width = root.clientWidth || innerWidth;
        const height = window.visualViewport?.height || innerHeight;
        return rect.width > 0 && rect.height > 0 && rect.left < width - 1 && rect.right > 1 &&
          rect.top < height - 1 && rect.bottom > 1 && style.visibility !== 'hidden' && style.display !== 'none';
      });
    }

    function externalLock() {
      const bodyStyle = getComputedStyle(document.body);
      return root.classList.contains(lockClass) || openingActive() || modalVisible() ||
        bodyStyle.position === 'fixed' || ['hidden', 'clip'].includes(bodyStyle.overflowY) ||
        ['hidden', 'clip'].includes(getComputedStyle(root).overflowY);
    }

    function holdAt(y) {
      const body = document.body;
      const width = body.getBoundingClientRect().width;
      lock = {
        x: window.scrollX, y: Math.max(0, y), restore: [], owned: [],
        viewport: {
          width: innerWidth,
          height: window.visualViewport?.height || innerHeight,
          scale: window.visualViewport?.scale || 1
        }
      };
      root.classList.add(lockClass);
      const properties = {
        position: 'fixed', top: `${-lock.y}px`, left: `${-lock.x}px`,
        // Freeze the actual pre-lock border box. Adding scrollbar padding to a
        // 100% body can rewrap earlier sections and move this chapter's anchor.
        width: `${width}px`, 'overflow-x': 'hidden', 'overflow-y': 'hidden', 'box-sizing': 'border-box'
      };
      Object.entries(properties).forEach(([property, value]) => {
        lock.restore.push(ownStyle(body, property, value));
        lock.owned.push([property, body.style.getPropertyValue(property)]);
      });
      // A programmatically opened dialog should also release our pause, even
      // when it was not preceded by a click or focus event.
      lockObserver = new MutationObserver(() => {
        if (!lock) return;
        if (openingActive() || modalVisible() || lock.owned.some(([property, value]) =>
          body.style.getPropertyValue(property) !== value ||
          body.style.getPropertyPriority(property) !== 'important')) finish();
      });
      lockObserver.observe(body, { attributes: true, attributeFilter: ['style', 'class'] });
      lockObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
    }

    function begin() {
      if (state !== 'waiting') return false;
      if (!scene.isConnected || motion.matches || window.Shopify?.designMode ||
          !image.complete || image.naturalWidth <= 0 ||
          decodedSource !== (image.currentSrc || image.src) || externalLock() ||
          (window.visualViewport && Math.abs(window.visualViewport.scale - 1) > .01)) {
        finish();
        return false;
      }
      const viewport = sizeSection();
      const y = window.scrollY + scene.getBoundingClientRect().top - viewport.top;
      state = 'running';
      // Independent watchdog: an animation failure must never strand the page.
      wait(finish, 4500);
      try {
        holdAt(y);
        scene.classList.remove('design-intro--armed');
        scene.classList.add('design-intro--running');
        animations.push(media.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 1400, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'backwards'
        }));
        animations.push(image.animate([{ transform: 'scale(1.025)' }, { transform: 'scale(1)' }], {
          duration: 1900, easing: 'cubic-bezier(.2,.65,.3,1)', fill: 'backwards'
        }));
        animations.push(copy.animate([
          { opacity: 0, transform: 'translateY(10px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { delay: headingDelay, duration: 1100, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' }));
        wait(allowScroll, headingDelay);
        wait(finish, 2100);
        return true;
      } catch (_) {
        finish();
        return false;
      }
    }

    function boundary() {
      return window.scrollY + scene.getBoundingClientRect().top - bounds().top;
    }

    function crosses(delta) {
      if (state !== 'waiting' || delta <= 0) return false;
      const stop = boundary();
      return window.scrollY <= stop + 1 && window.scrollY + delta >= stop - 1;
    }

    function nestedScroller(target, delta) {
      let element = target instanceof Element ? target : null;
      while (element && element !== document.body) {
        if (element.scrollHeight > element.clientHeight + 1 &&
            /auto|scroll/.test(getComputedStyle(element).overflowY) &&
            ((delta > 0 && element.scrollTop + element.clientHeight < element.scrollHeight - 1) ||
             (delta < 0 && element.scrollTop > 0))) return true;
        element = element.parentElement;
      }
      return false;
    }

    function onWheel(event) {
      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey) { finish(); return; }
      if (event.target.closest?.(interactive)) { if (state === 'running') finish(); return; }
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? innerHeight : 1);
      if (state === 'running') { event.preventDefault(); return; }
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || nestedScroller(event.target, delta)) return;
      if (crosses(delta) && begin()) event.preventDefault();
    }

    function onTouchStart(event) {
      if (event.touches.length !== 1) { finish(); finger = null; return; }
      const touch = event.touches[0];
      finger = { id: touch.identifier, x: touch.clientX, y: touch.clientY };
    }

    function onTouchMove(event) {
      if (event.defaultPrevented) return;
      if (event.touches.length !== 1) { finish(); return; }
      if (event.target.closest?.(interactive)) { if (state === 'running') finish(); return; }
      if (!finger) return;
      const touch = event.touches[0];
      if (touch.identifier !== finger.id) return;
      const delta = finger.y - touch.clientY;
      const horizontal = Math.abs(finger.x - touch.clientX) > Math.abs(delta);
      finger = { id: touch.identifier, x: touch.clientX, y: touch.clientY };
      if (state === 'running') { event.preventDefault(); return; }
      if (!horizontal && !nestedScroller(event.target, delta) && crosses(delta) && begin()) event.preventDefault();
    }

    function onScroll() {
      if (state === 'running') {
        // Body fixing covers scrollbar drags and iOS momentum too. Some engines
        // still move the root scroll offset; keep that offset at zero until free.
        if (window.scrollY !== 0 || window.scrollX !== 0) scrollExactly(0, 0);
        return;
      }
      if (state !== 'waiting') return;
      const y = window.scrollY;
      const stop = boundary();
      if (y > lastY + .5 && lastY <= stop + 1 && y >= stop - 1) begin();
      lastY = window.scrollY;
    }

    function intentionalInteraction(event) {
      if (event.target.closest?.(interactive)) finish();
    }

    function onKeyDown(event) {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape' || event.key === 'Tab' || event.ctrlKey || event.metaKey ||
          event.altKey || event.target.closest?.(interactive)) { finish(); return; }
      if (!scrollKeys.includes(event.key)) return;
      if (state === 'running') { event.preventDefault(); return; }
      const delta = event.key === 'End' ? Infinity : event.key === 'ArrowDown' ? 40 :
        event.key === 'PageDown' || (event.key === ' ' && !event.shiftKey) ? innerHeight * .88 : 0;
      if (delta > 0 && !nestedScroller(event.target, delta) && crosses(delta) && begin()) event.preventDefault();
    }

    function onHashChange() {
      const wasRunning = state === 'running';
      finish();
      // Hash navigation may have happened while the body was fixed. Once its
      // normal flow is restored, honor that destination rather than our anchor.
      if (wasRunning && location.hash) {
        let target;
        try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); }
        catch (_) { return; }
        if (target) {
          const restore = ownStyle(root, 'scroll-behavior', 'auto');
          try { target.scrollIntoView({ behavior: 'auto', block: 'start' }); }
          finally { restore(); }
        }
      }
    }

    scene.classList.add('design-intro--enhanced');
    sizeSection();
    if (cue) {
      cue.hidden = false;
      cue.addEventListener('click', () => {
        finish();
        const target = scene.closest('.design-story')?.querySelector('.design-story__scenes');
        if (!target) return;
        const top = window.scrollY + target.getBoundingClientRect().top - bounds().top - 16;
        if (motion.matches) scrollExactly(window.scrollX, top);
        else window.scrollTo({ left: window.scrollX, top, behavior: 'smooth' });
      }, { signal: lifetimeEvents.signal });
    }

    const resize = () => {
      if (state === 'running' && lock) {
        const saved = lock.viewport;
        const height = window.visualViewport?.height || innerHeight;
        const scale = window.visualViewport?.scale || 1;
        // Hiding a desktop scrollbar changes visualViewport.width only. That
        // is our own lock, not a screen resize or a request to abort the scene.
        if (innerWidth === saved.width && Math.abs(height - saved.height) < 2 &&
            Math.abs(scale - saved.scale) < .01) return;
        finish();
      }
      sizeSection();
      lastY = window.scrollY;
    };
    window.addEventListener('resize', resize, { signal: lifetimeEvents.signal });
    window.visualViewport?.addEventListener('resize', resize, { signal: lifetimeEvents.signal });
    window.addEventListener('pagehide', finish, { signal: lifetimeEvents.signal });
    window.addEventListener('pageshow', event => { if (event.persisted) finish(); }, { signal: lifetimeEvents.signal });
    document.addEventListener('visibilitychange', () => { if (document.hidden) finish(); }, { signal: lifetimeEvents.signal });
    window.addEventListener('beforeprint', finish, { signal: lifetimeEvents.signal });
    motion.addEventListener('change', finish, { signal: lifetimeEvents.signal });
    document.addEventListener('shopify:section:unload', event => {
      if (!scene.isConnected || event.target.contains(scene)) {
        finish();
        lifetimeEvents.abort();
      }
    }, { signal: lifetimeEvents.signal });

    // A direct link or a restored scroll position should never pull readers
    // backward. An unloaded/failed image is revealed normally, without a wait.
    if (!media || !image || !copy || !media.animate || !image.animate || !copy.animate ||
        motion.matches || window.Shopify?.designMode || location.hash || boundary() < window.scrollY - 1) {
      finish();
      return;
    }

    scene.classList.add('design-intro--armed');
    const signal = entranceEvents.signal;
    image.addEventListener('load', preparePhotograph, { signal });
    preparePhotograph();
    window.addEventListener('wheel', onWheel, { passive: false, capture: true, signal });
    window.addEventListener('touchstart', onTouchStart, { passive: true, capture: true, signal });
    window.addEventListener('touchmove', onTouchMove, { passive: false, capture: true, signal });
    window.addEventListener('touchend', () => { finger = null; }, { passive: true, signal });
    window.addEventListener('touchcancel', () => { finger = null; if (state === 'running') finish(); }, { passive: true, signal });
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('hashchange', onHashChange, { signal });
    document.addEventListener('focusin', event => {
      // Focusing the browser's page surface is not a request to skip a chapter.
      if (event.target === document.body || event.target === root) return;
      if (state === 'running' || event.target.closest?.(interactive)) finish();
    }, { capture: true, signal });
    document.addEventListener('keydown', onKeyDown, { capture: true, signal });
    ['pointerdown', 'click'].forEach(name => {
      document.addEventListener(name, intentionalInteraction, { capture: true, signal });
    });
    image.addEventListener('error', finish, { signal });
  }

  document.querySelectorAll('[data-design-intro-entrance]').forEach(mount);
})();
